import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import type { FileType } from "@/common/types/common";
import {
  sampleAdd,
  sampleDel,
  sampleList,
  samplePut,
  sampleModelCSV,
  sampleUpload,
} from "@/common/api/rest/sample";
import type {
  SampleListType,
  SamplePayloadType,
  SampleType,
} from "@/common/types/sample";

import { RegisterTheme } from "@/common/themes";
import { fileUploadForm, sampleForm } from "@/components";
import {
  baseAvatar,
  baseTable,
  baseConfirm,
  baseModal,
  svgIcon,
} from "@/common/components";

import { statusIcons, orderIcons } from "@/common/settings/register";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t, tm } = useI18n();
const route = useRoute();
const router = useRouter();

const tableData = ref<SampleListType[]>([]);

const selected = ref<SampleListType | null>(null);
const payload = ref<SamplePayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isEdit = ref(false);
const loading = ref(false);
const showModal = ref(false);
const showConfirmDel = ref(false);
const showModalUpload = ref(false);

const id = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "logo", label: null },
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
]);

const modalTitle = computed(() =>
  isEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.sample"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.sample"),
      }).toUpperCase(),
);

const itemStatus = computed(
  () => tm("enums.item_status") as Record<string, string>,
);

function fetchData() {
  sampleList(id.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

function add() {
  showModal.value = true;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  sampleAdd(id.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        router.push(`/${id.value}/register/sample/edit/${data.sample_id}`);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: SampleListType) {
  router.push(`/${id.value}/register/sample/edit/${data.sample_id}`);
}

function editConfirm() {
  if (!selected.value && !payload.value) return;

  store.commit("SET_LOADING", true);

  samplePut(
    id.value,
    selected.value?.sample_id as string,
    payload.value as SamplePayloadType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        closeModal();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submit() {
  if (!isEdit.value) addConfirm();
  else editConfirm();
}

function closeModal() {
  payload.value = null;
  showModal.value = false;
  isEdit.value = false;
}

function remove(data: SampleListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);

  sampleDel(id.value, selected.value.sample_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  search.value = value;
  tableData.value = [];
  loading.value = true;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  loading.value = true;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  store.commit("SET_LOADING", true);
  sampleUpload(id.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  sampleModelCSV(id.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "sample" }, {
      title: _withCtx(() => [
        _createVNode(_unref(svgIcon), {
          icon: "samples",
          size: "2rem"
        }),
        _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.samples")), 1)
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(baseTable), {
          header: tableHeader.value,
          data: tableData.value,
          limit: limit.value,
          page: page.value,
          pages: pages.value,
          total: total.value,
          searched: searched.value,
          options: true,
          edit: true,
          del: true,
          upload: true,
          loading: loading.value,
          "onChange:page": changePage,
          onSearching: searchItem,
          "onBtn:add": add,
          "onBtn:edit": edit,
          "onBtn:del": remove,
          "onBtn:upload": _cache[0] || (_cache[0] = ($event: any) => (uploadCsv()))
        }, {
          logo: _withCtx(({ data }) => [
            _createVNode(_unref(baseAvatar), {
              url: data.avatar?.small,
              size: "1.75rem",
              background: "#ffffff"
            }, null, 8, ["url"])
          ]),
          status: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(itemStatus.value[data.status]), 1)
          ]),
          _: 1
        }, 8, ["header", "data", "limit", "page", "pages", "total", "searched", "loading"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseConfirm), {
      show: showConfirmDel.value,
      title: 
      _unref(t)('common.labels.remove', {
        concat: _unref(t)('common.labels.sample'),
      }).toUpperCase()
    ,
      message: 
      _unref(t)('common.texts.remove_message', {
        concat: selected.value?.code,
      })
    ,
      onCancel: removeCancel,
      onConfirm: removeConfirm
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseModal), {
      show: showModal.value,
      title: modalTitle.value,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.save'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[2] || (_cache[2] = ($event: any) => (closeModal())),
      onConfirm: _cache[3] || (_cache[3] = ($event: any) => (submit()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(sampleForm), {
          data: payload.value,
          onUpdateData: _cache[1] || (_cache[1] = ($event: any) => (payload.value = $event))
        }, null, 8, ["data"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseModal), {
      show: showModalUpload.value,
      title: 
      _unref(t)('common.labels.upload', {
        concat: _unref(t)('common.labels.sample'),
      }).toUpperCase()
    ,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.send'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[6] || (_cache[6] = ($event: any) => (uploadCancel())),
      onConfirm: _cache[7] || (_cache[7] = ($event: any) => (uploadConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(fileUploadForm), {
          extensions: fileExtensions.value,
          model: true,
          onUpdateData: _cache[4] || (_cache[4] = ($event: any) => (fileUpload.value = $event)),
          onModelDownload: _cache[5] || (_cache[5] = ($event: any) => (downloadModel()))
        }, null, 8, ["extensions"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"])
  ], 64))
}
}

})